<script lang="ts">
export default {
  name: 'UISelect',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
} from 'reka-ui';

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface Props {
  modelValue?: string;
  options: Option[];
  placeholder?: string;
  class?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select an option',
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const selectedValue = computed({
  get: () => props.modelValue || 'empty',
  set: (value) => emit('update:modelValue', value === 'empty' ? '' : value as string),
});

const selectedOption = computed(() => {
  return props.options.find(option => option.value === props.modelValue);
});
</script>

<template>
  <SelectRoot v-model="selectedValue" :disabled="disabled">
    <SelectTrigger
      :class="
        cn(
          'tw:border-input tw:bg-background tw:ring-offset-background tw:placeholder:text-muted-foreground tw:focus:ring-ring data-[state=open]:tw:ring-2 data-[state=open]:tw:ring-ring data-[state=open]:tw:ring-offset-2 tw:flex tw:h-10 tw:w-full tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-solid tw:px-3 tw:py-2 tw:text-sm tw:focus:ring-2 tw:focus:ring-offset-2 tw:focus:outline-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
          props.class,
        )
      ">
      <SelectValue :placeholder="placeholder">
        <template v-if="selectedOption">
          <div class="tw:flex tw:gap-1 tw:items-center">
            <div>{{ selectedOption.label }}</div>
            <div class="tw:text-xs tw:italic" >{{ selectedOption.description }}</div>
          </div>
        </template>
      </SelectValue>
      <SelectIcon as-child>
        <ChevronDownIcon class="data-[state=open]:tw:rotate-180 tw:h-4 tw:w-4 tw:opacity-50 tw:transition-transform" />
      </SelectIcon>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        class="tw:border-input tw:bg-popover tw:text-popover-foreground tw:animate-in tw:fade-in-0 tw:zoom-in-95 tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=closed]:zoom-out-95 tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2 tw:z-9999 tw:min-w-32 tw:overflow-hidden tw:rounded-md tw:border tw:bg-base-100 tw:shadow-md"
        :side-offset="5">
        <SelectViewport class="tw:p-1">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value || 'empty'"
            :class="
              cn(
                'tw:hover:text-accent-foreground tw:data-highlighted:text-accent-foreground tw:data-[state=checked]:text-accent-foreground/50 tw:relative tw:flex tw:cursor-pointer tw:items-center tw:rounded-sm tw:px-2 tw:py-1.5 tw:text-sm tw:transition-colors tw:duration-100 tw:outline-none tw:select-none tw:hover:bg-secondary/30 tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50 tw:data-highlighted:bg-secondary/30 tw:data-[state=checked]:bg-secondary/50',
              )
            ">
            <SelectItemText>
              <div>
                <div class="tw:font-medium">{{ option.label }}</div>
                <div v-if="option.description" class="tw:text-muted-foreground tw:text-xs">
                  {{ option.description }}
                </div>
              </div>
            </SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
